import posthog from 'posthog-js';

import type { IVerifyUser } from '@/features/auth/auth.types';

const POSTHOG_KEY = import.meta.env.VITE_APP_POSTHOG_KEY ?? '';

// Same-origin path proxied to PostHog by Vite (see vite.config.ts), not the
// upstream host — a direct eu.i.posthog.com call is blocked by ad blockers.
const POSTHOG_HOST = '/ingest';

// Where the dashboard lives. Without this, PostHog assumes the dashboard sits
// at api_host and the toolbar's "open in PostHog" links point at our proxy.
const POSTHOG_UI_HOST = 'https://eu.posthog.com';

// Analytics only runs where a project key is configured, so local/dev work
// never pollutes the shared PostHog project. Mirrors how booking.gtm.ts gates
// on a mapped container id.
export const IS_POSTHOG_ENABLED = Boolean(POSTHOG_KEY);

let initialized = false;

export const initPostHog = () => {
  if (!IS_POSTHOG_ENABLED) return;
  if (initialized || typeof window === 'undefined') return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: POSTHOG_UI_HOST,
    // This is a React Router SPA: PostHog's built-in pageview only fires on
    // the initial hard load, so we send them ourselves from RootLayout on
    // every navigation. Leaving the default on would double-count the first.
    capture_pageview: false,
    capture_pageleave: true,
    person_profiles: 'identified_only',
    persistence: 'localStorage+cookie',
    // Session replay is toggled on per project in the PostHog dashboard;
    // this only pins the masking level. Deliberately the minimum: input
    // values are hidden (PostHog's default), on-screen text is not. Replays
    // of the CMS therefore show customer names and booking details as they
    // appear to the operator. Tighten with `maskTextSelector` if that ever
    // needs to change.
    session_recording: {
      maskAllInputs: true,
    },
  });

  posthog.register({
    environment: import.meta.env.VITE_APP_ENV ?? 'development',
  });

  initialized = true;
};

export const capturePageview = (path: string) => {
  if (!IS_POSTHOG_ENABLED) return;
  posthog.capture('$pageview', {
    $current_url: window.location.href,
    pathname: path,
  });
};

// Ties events to the CMS account. Called once per user from RootLayout rather
// than at login, because the user object only lands in the store after the
// protected loader has verified the session.
export const identifyUser = (user: IVerifyUser) => {
  if (!IS_POSTHOG_ENABLED || !user.id) return;
  posthog.identify(user.id, {
    email: user.email,
    name: user.name,
    role: user.role?.name,
    company_id: user.company?.id,
    company_name: user.company?.name,
  });
};

export const resetUser = () => {
  if (!IS_POSTHOG_ENABLED) return;
  posthog.reset();
};

export { posthog };
